import { Box, Stack, Tab } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useLangContext } from "../../../context/langContext";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import CustomTable from "../../common/tables/Table";
import { useNotificationContext } from "../../../context/notificationContext";
import errorResolver from "../../../utils/error-resolver";
import {
  LogListColumnFilterable,
  LogListColumnSortable,
} from "../../../services/Endpoints/logs/LogListEndpoints";
import FilterLinkAction from "../../common/filter-action/FilterLinkAction";
import { routerPaths } from "../../../constants/Paths";
import { dateFormatter } from "../../../utils/date-formatter";
import {
  CallCalling,
  CallReceived,
  DocumentText,
  EmptyWallet,
  Microphone,
} from "iconsax-react";
import { useUrl } from "../../../hooks/urlEncoding";
import { WalletListColumnFilterable } from "../../../services/Endpoints/wallet/WalletListEndpoint";
import {
  FilterOperatorEnum,
  SortDirectionEnum,
} from "../../../services/Schemas/ListQueryString";
import {
  BroadcastingListColumnFilterable,
  BroadcastingListColumnSortable,
} from "../../../services/Endpoints/broadcast";
import LinkWithPopover from "../common/LinkWithPopover";
import {
  CallDetailListColumnFilterable,
  CallDetailListColumnSortable,
  CallDetailListRequest,
  PbxCallDetailStatusEnum,
} from "../../../services/Endpoints/broadcast/PbxCallDetails";
import InfoBadge, { OriginEnum } from "../../common/info-badge/InfoBadge";
import {
  NumberListColumnFilterable,
  NumberListColumnSortable,
  NumberListRequest,
  NumberStatusEnum,
} from "../../../services/Endpoints/broadcast/NumbersEndpoint";
import { useCanAccess } from "../../../hooks/canAccess";
import { AccessEnums } from "../../../constants/accessEnum";
import useTableSchema from "../../../hooks/useTableSchema";
import { ITableSchema } from "../../../utils/types";
import NumberFilter from "../../common/tables/filters/NumberFilter";
import TextFilter from "../../common/tables/filters/TextFilter";
import DateTimeFilter from "../../common/tables/filters/DateTimeFilter";
import SelectionFilter from "../../common/tables/filters/SelectionFilter";
import useUrlSort from "../../../hooks/useUrlSort";
import { SendCallsListColumnFilterable, SendCallsListColumnSortable } from "../../../services/Endpoints/send-calls/SendCallsEndpoint";

const statusOptions = {
  [NumberStatusEnum.CREATED]: "ایجاد شده",
  [NumberStatusEnum.WAITING_FOR_SEND]: "در انتظار ارسال",
  [NumberStatusEnum.SENDING]: "در حال ارسال",
  [NumberStatusEnum.FAILED]: "ناموفق",
  [NumberStatusEnum.ANSWERED]: "پاسخ داده شده",
  [NumberStatusEnum.BLOCKED]: "بلاک شده",
  [NumberStatusEnum.NO_ANSWER]: "بی پاسخ",
  [NumberStatusEnum.BUSY]: "مشغول",
  [NumberStatusEnum.REJECTED]: "رد شده",
  [NumberStatusEnum.UNREACHABLE]: "خارج از دسترس",
  [NumberStatusEnum.DEVICE_OFF]: "خاموش",
  [NumberStatusEnum.INVALID_NUMBER]: "شماره نامعتبر",
  [NumberStatusEnum.CANCELED]: "کنسل شده",
  [NumberStatusEnum.RETRY]: "تلاش مجدد",
  [NumberStatusEnum.PENDING]: "در انتظار ارسال",
};

const _columns: ITableSchema[] = [
  {
    key: "id",
    label: "شناسه",
    minWidth: 100,
    align: "center",
    FilterComponent: NumberFilter,
    sortable: true,
  },
  {
    key: "number",
    label: "شماره",
    minWidth: 100,
    align: "center",
    FilterComponent: TextFilter,
  },
  {
    key: "tries",
    label: "تعداد تلاش",
    minWidth: 100,
    align: "center",
    FilterComponent: NumberFilter,
  },

  {
    key: "total_talk_time",
    label: "طول تماس",
    minWidth: 170,
    align: "center",
    FilterComponent: NumberFilter,
  },
  {
    key: "total_waiting_time",
    label: "زمان انتظار",
    minWidth: 170,
    align: "center",
    FilterComponent: NumberFilter,
  },
  {
    key: "total_cast",
    label: "هزینه تماس",
    minWidth: 170,
    align: "center",
    FilterComponent: NumberFilter,
  },
  {
    key: "status",
    label: "وضعیت",
    minWidth: 170,
    align: "center",
    FilterComponent: SelectionFilter,
    options: statusOptions,
  },
  {
    key: "voice_broadcast_id",
    label: "آیدی کمپین",
    minWidth: 170,
    align: "center",
    FilterComponent: NumberFilter,
  },
  {
    key: "created_at",
    label: "تاریخ ایجاد",
    minWidth: 170,
    align: "center",
    format: (value: string) =>
      value && dateFormatter({ lang: "fa", date: value }),
    FilterComponent: DateTimeFilter,
  },

  {
    key: "operations",
    label: "عملیات",
    minWidth: 170,
    align: "center",
  },
];
interface IPayment {
  id: number;
  number: string;
  total_talk_time: number | null;
  call_input: string | null;
  total_cast: number;
  tries: number;
  total_waiting_time: number | null;

  extension: string | null;
  voice_broadcast_id: number;
  created_at: string | null;
  status: ReactNode;
  operations: string | ReactNode;
}

export interface ISupportTabsComponent {
  walletReload: boolean;
  setWalletReload: (payload: boolean) => void;
}

export default function CallToNumberDetailTableComponent({
  walletReload,
  setWalletReload,
}: Readonly<ISupportTabsComponent>) {
  const { queryStringUrl, parseQueryString, setParamWithSearchParams } =
    useUrl();
  const parsed = parseQueryString();
  const initialPage = parsed.page ? Number(parsed.page) : 1;
  const initialRows = parsed.count ? Number(parsed.count) : 10;
  const [value, setValue] = useState("1");
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);

  const [dataPayment, setDataPayment] = useState<IPayment[] | []>([]);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const [waiting, setWaiting] = useState(false);
  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();
  const { can } = useCanAccess();
  const { filtersSchema, columns } = useTableSchema(_columns);
  const { parseUrlSorts, applyUrlSorts } = useUrlSort();
  const sorts = parseUrlSorts();

  const getDataPayment = async () => {
    try {
      setWaiting(true);
      const paymentListRequest = new NumberListRequest(protectedInstance);
      const resp = await paymentListRequest.list({
        page,
        count: rowsPerPage,
        ...parseQueryString(),
      });
      setTotal(resp.data.total);
      const transactions = resp.data.voice_broadcast_numbers.map(
        ({
          id,
          created_at,
          call_input,
          number,
          total_cast,
          tries,
          total_talk_time,
          total_waiting_time,
          status,
          extension,
          voice_broadcast_id,
        }) => ({
          id,
          total_cast,
          total_talk_time,
          call_input,
          number,
          tries,
          total_waiting_time,
          extension,
          voice_broadcast_id,
          created_at,
          status: (
            <InfoBadge
              origin={OriginEnum.BROADCASTING_NUMBER}
              status={status as "pending"}
            />
          ),
          operations: (
            <Stack
              direction="row"
              gap="3px"
              alignItems="center"
              justifyContent="center"
            >
              <LinkWithPopover
                disabled={!can(AccessEnums.AdminSendCallsList)}
                title="درخواست تماس"
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                      // backgroundColor: (theme) => theme.palette.others.doneBg,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.SEND_CALLS, {
                    filters: {
                      [SendCallsListColumnFilterable.VOICE_BROADCAST_ID_NUMBER]: {
                        column:
                          SendCallsListColumnFilterable.VOICE_BROADCAST_ID_NUMBER,
                        operator: FilterOperatorEnum.EQUAL,
                        value: id,
                      },
                    },
                    sorts: {
                      [SendCallsListColumnSortable.ID]: {
                        column: SendCallsListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <CallReceived size="20" />
                </Stack>
              </LinkWithPopover>
              <LinkWithPopover
                disabled={!can(AccessEnums.AdminPbxCallDetailRecordsList)}
                title="تماس ها"
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                      // backgroundColor: (theme) => theme.palette.others.doneBg,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.PBX_CALL_DETAIL, {
                    filters: {
                      [CallDetailListColumnFilterable.VOICE_BROADCAST_NUMBER_ID]:
                        {
                          column:
                            CallDetailListColumnFilterable.VOICE_BROADCAST_NUMBER_ID,
                          operator: FilterOperatorEnum.EQUAL,
                          value: id,
                        },
                    },
                    sorts: {
                      [CallDetailListColumnSortable.ID]: {
                        column: CallDetailListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <CallCalling size="20" />
                </Stack>
              </LinkWithPopover>

              <LinkWithPopover
                disabled={!can(AccessEnums.AdminVoiceBroadcastsList)}
                title="پیام صوتی"
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                      // backgroundColor: (theme) => theme.palette.others.doneBg,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.VOICE_BROADCASTS, {
                    filters: {
                      [BroadcastingListColumnFilterable.ID]: {
                        column: BroadcastingListColumnFilterable.ID,
                        operator: FilterOperatorEnum.EQUAL,
                        value: voice_broadcast_id,
                      },
                    },
                    sorts: {
                      [BroadcastingListColumnSortable.ID]: {
                        column: BroadcastingListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <Microphone size="20" />
                </Stack>
              </LinkWithPopover>
              <LinkWithPopover
                title="لاگ ها"
                disabled={!can(AccessEnums.AdminLogsList)}
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                      // backgroundColor: (theme) => theme.palette.others.doneBg,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.LOGS, {
                    filters: {
                      [LogListColumnFilterable.REFERENCE_MODEL]: {
                        column: LogListColumnFilterable.REFERENCE_MODEL,
                        operator: FilterOperatorEnum.EQUAL,
                        value: id,
                      },
                      [LogListColumnFilterable.MODEL]: {
                        column: LogListColumnFilterable.MODEL,
                        operator: FilterOperatorEnum.EQUAL,
                        value: "voice_broadcast_numbers",
                      },
                    },
                    sorts: {
                      [LogListColumnSortable.ID]: {
                        column: LogListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <DocumentText size="20" />
                </Stack>
              </LinkWithPopover>
            </Stack>
          ),
        })
      );
      setDataPayment(transactions);
      setWaiting(false);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getDataPayment();
  }, [walletReload, page, rowsPerPage, value, location.search]);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setParamWithSearchParams("page", String(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRows = +event.target.value;
    setRowsPerPage(newRows);
    setParamWithSearchParams("count", String(newRows));
  };

  return (
    <Box sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}>
      <CustomTable
        reloadAction={() => setWalletReload(!walletReload)}
        hasWaiting={waiting}
        hasSearchFilter
        hasBottomPagination
        hasTopPagination
        columns={columns}
        count={total}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        rows={dataPayment}
        rowsPerPage={rowsPerPage}
        filtersSchema={filtersSchema}
        onApplySorts={applyUrlSorts}
        sorts={sorts}
      />
    </Box>
  );
}
